import { TixologiResumedEvent } from "@punchup/data/legacy/tixologi";
import dayjs from "@punchup/utils/dayjs";

// Parse an ISO instant in the given IANA timezone, or tz-naive when omitted.
const inZone = (iso: string, tz?: string) =>
  tz ? dayjs(iso).tz(tz) : dayjs(iso);

/**
 * A single sitting cannot be a multi-day event, however the clock falls. A span
 * shorter than this is one show night billed on its START date: a Saturday
 * 10:30pm set booked "10:30 PM – 12:00 AM" ends on Sunday's clock but is
 * Saturday's show, not a two-day run. Above it the span is a genuine run and
 * every calendar day it touches counts.
 *
 * Twelve hours is the smallest cutoff that classifies both extremes correctly:
 * it clears the longest single sitting that crosses midnight (a 6pm–2am or
 * 8pm–4am night, 8h) while staying at-or-below the shortest genuine two-day
 * run (a Sat 10pm → Sun 10am overnight, 12h — `<` keeps that one multi-day).
 */
const SINGLE_NIGHT_MAX_HOURS = 12;

/**
 * The two calendar days a span should be DISPLAYED as, in `tz` (or tz-naive
 * when omitted).
 *
 * The multi-day decision is made on DURATION, not on where the endpoints fall
 * against midnight — a duration is identical in every timezone, so a short show
 * reads as one night even when the event row carries no `time_zone`. An
 * inverted span (negative duration) lands here too and reads as a single day.
 */
function spanDays(startIso: string, endIso: string, tz?: string) {
  const startAt = inZone(startIso, tz);
  const endAt = inZone(endIso, tz);
  const start = startAt.startOf("day");
  const isSingleNight =
    endAt.diff(startAt, "hour", true) < SINGLE_NIGHT_MAX_HOURS;
  return { start, end: isSingleNight ? start : endAt.startOf("day") };
}

/**
 * Number of distinct calendar days a start→end span touches, inclusive (Aug
 * 14→15 is 2, a same-day show is 1). Pass `tz` (e.g. an event's `time_zone`) so
 * an instant near midnight counts in the local day, not UTC — otherwise a
 * late-evening show on a multi-day run counts an extra day. A single show that
 * merely runs past midnight is always 1 (see `SINGLE_NIGHT_MAX_HOURS`). Never
 * returns less than 1.
 */
export function countEventDays(
  startIso: string,
  endIso: string,
  tz?: string
): number {
  const { start, end } = spanDays(startIso, endIso, tz);
  return Math.max(1, end.diff(start, "day") + 1);
}

export interface MultiDayEventSpan {
  start: string;
  end: string;
  tz?: string;
  /** Distinct calendar days the span touches, always > 1. */
  dayCount: number;
}

/**
 * The multi-day span of a Tixologi event, or `null` when the event is
 * single-day, missing, or not yet loaded. Guards the event read the way the
 * poster-image code does — the fetch sometimes yields the string "event not
 * found" instead of an object. Both the carousel and the tour-date row derive
 * their range display from this one decision.
 */
export function multiDayEventSpan(
  event: TixologiResumedEvent | null | undefined
): MultiDayEventSpan | null {
  if (
    !event ||
    typeof event !== "object" ||
    !("end_date" in event) ||
    !event.start_date ||
    !event.end_date
  ) {
    return null;
  }
  const tz = event.time_zone || undefined;
  const dayCount = countEventDays(event.start_date, event.end_date, tz);
  return dayCount > 1
    ? { start: event.start_date, end: event.end_date, tz, dayCount }
    : null;
}

/**
 * Keep a numeric day range ("24–26") from wrapping across lines — the en-dash is
 * otherwise a valid break point. The \u2060 (word joiner) chars bracket only the
 * dash, so a break can still fall at the space between the month and the range.
 */
export function nonBreakingDayRange(label: string): string {
  return label.replace(/(\d)–(\d)/g, "$1\u2060–\u2060$2");
}

/**
 * Human date-range label for a group card. Collapses to the smallest form:
 *   same day    -> "Aug 1"
 *   same month  -> "Aug 1–2"
 *   same year   -> "Aug 1 – Sep 2"
 *   else        -> "Dec 30, 2026 – Jan 2, 2027"
 * Pass `tz` to collapse/format in that timezone rather than UTC-naive. A single
 * show that only runs past midnight collapses to its start date, not a range.
 */
export function formatVenueShowGroupDateRange(
  startIso: string,
  endIso: string,
  tz?: string
): string {
  const { start: s, end: e } = spanDays(startIso, endIso, tz);
  if (s.isSame(e, "day")) return s.format("MMM D");
  if (s.isSame(e, "month")) return `${s.format("MMM D")}–${e.format("D")}`;
  if (s.isSame(e, "year")) {
    return `${s.format("MMM D")} – ${e.format("MMM D")}`;
  }
  return `${s.format("MMM D, YYYY")} – ${e.format("MMM D, YYYY")}`;
}

/**
 * Label for the grid card's date badge. Two shapes so the badge never
 * over-emphasizes an end date:
 *   same month  -> { month: "AUG", day: "1–3" }      — small month label over
 *                  the emphasized day/range (the individual card's chip mold).
 *   cross month -> { start: "JUL 14 –", end: "DEC 29" } — two equal-weight
 *                  lines that read top-to-bottom as one span.
 */
export function formatVenueShowGroupDateBadge(
  startIso: string,
  endIso: string,
  tz?: string
): { month: string; day: string } | { start: string; end: string } {
  const { start: s, end: e } = spanDays(startIso, endIso, tz);
  if (s.isSame(e, "month")) {
    return {
      month: s.format("MMM").toUpperCase(),
      day: s.isSame(e, "day")
        ? s.format("D")
        : `${s.format("D")}–${e.format("D")}`,
    };
  }
  return {
    start: `${s.format("MMM D").toUpperCase()} –`,
    end: e.format("MMM D").toUpperCase(),
  };
}
